// components/mobile/skills/DirectCourseChat.tsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import MobileSkillsDetailView from './MobileSkillsDetailView';
import { POST } from 'api/index';
import { useTranslation } from 'react-i18next';

interface Skill {
  id: number;
  title: string;
  thumbnail?: string;
  image?: string;
  progress: number;
}

const DirectCourseChat: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const [skill, setSkill] = useState<Skill | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();


  const token = window.localStorage.getItem("currentUserToken");

  useEffect(() => {
    const loadSkill = async () => {
      if (!courseId || !token) {
        setError("Kurs-ID oder Token fehlt");
        setLoading(false);
        return;
      }

      try {
        // Erst versuchen aus lastBookedCourse zu laden (für direkte Navigation)
        const lastBookedCourse = localStorage.getItem("lastBookedCourse");
        if (lastBookedCourse === courseId) {
          // Kurze Wartezeit für den bookCourse Prozess
          await new Promise(resolve => setTimeout(resolve, 500));
        }

        // Kurs-Details laden (immer versuchen, unabhängig von MySkills)
        const body = new URLSearchParams();
        body.append("userToken", token);
        body.append("courseId", courseId);

        const [courseResponse, progressResponse] = await Promise.all([
          POST("getCourseDetails", body), // Falls dieser Endpoint existiert
          POST("getUserCoursePoints", body)
        ]);

        // Falls getCourseDetails nicht existiert, fallback zu getCourses
        let courseData = null;
        if (courseResponse.status === 200) {
          courseData = courseResponse.result;
        } else {
          // Fallback: Alle Kurse laden und den richtigen finden
          const allCoursesBody = new URLSearchParams();
          allCoursesBody.append("userToken", token);
          const allCoursesResponse = await POST("getCourses", allCoursesBody);
          
          if (allCoursesResponse.status === 200) {
            courseData = allCoursesResponse.result.find((course: any) => 
              course.id.toString() === courseId
            );
          }
        }

        if (!courseData) {
          setError("Kurs nicht gefunden");
          setLoading(false);
          return;
        }

        // MySkills nach dem Laden der Kurs-Details prüfen/aktualisieren
        const mySkills = JSON.parse(localStorage.getItem("mySkills") || "[]");
        if (!mySkills.includes(Number(courseId))) {
          // Falls nicht in MySkills, trotzdem den Kurs anzeigen (da er von Skillboard kommt)
          console.log("Kurs nicht in MySkills, aber von Skillboard aufgerufen");
        }

        const progress = progressResponse.status === 200 
          ? progressResponse.course_progress?.progress || 0 
          : 0;

        setSkill({
          id: Number(courseId),
          title: courseData.title,
          thumbnail: courseData.thumbnail || courseData.image,
          image: courseData.image,
          progress: progress
        });

      } catch (error) {
        console.error("Error loading skill:", error);
        setError("Fehler beim Laden des Kurses");
      } finally {
        setLoading(false);
      }
    };

    loadSkill();
  }, [courseId, token]);

  const handleClose = () => {
    navigate('/myskills', { replace: true });
  };

  const handleDelete = async () => {
    if (!skill) return;
    
    try {
      // Delete logic hier - du kannst die deleteUserCourse Funktion hier implementieren
      const body = new URLSearchParams();
      body.append("userToken", token || "");
      body.append("courseId", skill.id.toString());
      
      const response = await POST("removeUserFromCourse", body);
      if (response.status === 200) {
        // Aus localStorage entfernen
        const mySkills = JSON.parse(localStorage.getItem("mySkills") || "[]");
        const updatedSkills = mySkills.filter((id: number) => id !== skill.id);
        localStorage.setItem("mySkills", JSON.stringify(updatedSkills));
        
        navigate('/myskills', { replace: true });
      }
    } catch (error) {
      console.error("Error deleting course:", error);
    }
  };

  const updateProgress = () => {
    // Diese Funktion wird von MobileSkillsDetailView aufgerufen
    // Wenn du den Progress aktualisieren musst, mach es hier
    // Eventuell musst du die aktuellen Daten neu laden
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#f0f2f5'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !skill) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#f0f2f5',
        p: 3
      }}>
        <Typography variant="h6" color="error" gutterBottom>
          {error || "Kurs nicht gefunden"}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Gehe zurück zur Kursübersicht
        </Typography>
        <Box
          onClick={() => navigate('/myskills')}
          sx={{
            px: 3,
            py: 1.5,
            backgroundColor: 'primary.main',
            color: 'white',
            borderRadius: 2,
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'primary.dark'
            }
          }}
        >
          Zurück zu MySkills
        </Box>
      </Box>
    );
  }

  return (
    <MobileSkillsDetailView 
      skill={skill}
      onClose={handleClose}
      onDelete={handleDelete}
      updateProgress={updateProgress}
    />
  );
};

export default DirectCourseChat;